import {logError} from '../../../common/logging';

import {getHTMLTextLength} from './utils';

/**
 * NOTE: These steps are ordered, and should not be rearranged.
 *       The order is used to determine when to show/save the discount code.
 */
export enum Step {
  // User found and shown the authorize form (6-digit code or 1-click continue)
  AuthorizeShown = 'authorize-shown',
  // User has authorized successfully (entered 6-digit code or clicked 1-click continue)
  AuthorizeSuccess = 'authorize-success',
  // User is shown the consent screen with "Sign up with [phone]" button.
  PhoneConsentShown = 'phone-consent-shown',
  // User declined phone consent (I do not want texts).
  PhoneConsentDeclined = 'phone-consent-declined',
  // User clicked the "Sign up with [phone]" button to provide phone consent.
  PhoneConsentConfirmed = 'phone-consent-confirmed',
  // User shown reminder to check their phone for SMS opt-in.
  CheckYourTexts = 'check-your-texts',
  // Partner saves / shows the discount code, we never do.
  Never = 'never',
}

export const STEP_ORDER = Object.values(Step);

export const PHONE_CAPTURE_STEPS = [
  Step.PhoneConsentShown,
  Step.PhoneConsentConfirmed,
  Step.Never,
];

export const SHOW_DISCOUNT_AT_OPTIONS = [
  Step.AuthorizeShown,
  Step.AuthorizeSuccess,
  Step.PhoneConsentShown,
  Step.PhoneConsentConfirmed,
  Step.Never,
];

export const SAVE_DISCOUNT_AT_OPTIONS = [
  Step.AuthorizeSuccess,
  Step.PhoneConsentConfirmed,
  Step.Never,
];

export function getDiscountAuthVersion({
  phoneCapture,
  showDiscountAt,
  saveDiscountAt,
}: {
  phoneCapture: boolean;
  showDiscountAt: Step;
  saveDiscountAt: Step;
}) {
  if (phoneCapture) {
    return `PHONE_CAPTURE_SHOWN_AT_${showDiscountAt}_SAVED_AT_${saveDiscountAt}`
      .toUpperCase()
      .replace(/[-]/g, '_');
  }
  return 'EMAIL_CAPTURE';
}

export function shouldSaveDiscount(
  step: Step,
  saveDiscountAt: Step,
  discountSaved: boolean,
  discountSaveTriggered: boolean,
): boolean {
  return (
    !discountSaved &&
    !discountSaveTriggered &&
    step &&
    step !== Step.PhoneConsentDeclined &&
    STEP_ORDER.indexOf(step) >= STEP_ORDER.indexOf(saveDiscountAt)
  );
}

/**
 * Returns `true` if the discount code should be visible at the given step. That is, if the
 * given step is at or after the _showDiscountAt config.
 *
 * @param step The provided step.
 * @param showDiscountAt The step at which the discount code should be shown.
 * @returns true if the discount code should be visible at the given step, false otherwise.
 */
export function isDiscountVisible(step: Step, showDiscountAt: Step): boolean {
  if (
    step === Step.PhoneConsentDeclined &&
    showDiscountAt === Step.PhoneConsentConfirmed
  ) {
    return false;
  }
  return STEP_ORDER.indexOf(showDiscountAt) <= STEP_ORDER.indexOf(step);
}

/**
 * Returns `true` if the given step is a valid step for the _showDiscountAt config.
 *
 * @param step The provided step.
 * @returns true if the given step is a valid step for the _showDiscountAt config, false otherwise.
 */
export function isValidShowDiscountStep(step?: string | null) {
  return Boolean(step && (SHOW_DISCOUNT_AT_OPTIONS as string[]).includes(step));
}

/**
 * Returns `true` if the given step is a valid step for the _saveDiscountAt config.
 *
 * @param step The provided step.
 * @returns true if the given step is a valid step for the _saveDiscountAt config, false otherwise.
 */
export function isValidSaveDiscountStep(step?: string | null) {
  return Boolean(step && (SAVE_DISCOUNT_AT_OPTIONS as string[]).includes(step));
}

export const MAX_DISCLOSURE_TEXT_LENGTH = 500;

/**
 * Validates the component configuration. Called after all attributes are passed and immediately before
 * the component is rendered. In case of a failed validation, an error is thrown and the component is
 * not rendered.
 */
export function validateConfig({
  isPhoneCapture,
  phoneCaptureDisclosureText,
  saveDiscountAt,
  showDiscountAt,
}: {
  isPhoneCapture: boolean;
  phoneCaptureDisclosureText?: string;
  saveDiscountAt: Step;
  showDiscountAt: Step;
}) {
  if (isPhoneCapture && !phoneCaptureDisclosureText) {
    throw new Error(
      `A valid ${Attribute.PhoneCaptureDisclosureText} must be provided when ${Attribute.PhoneCapture} is enabled.`,
    );
  }
  if (
    phoneCaptureDisclosureText &&
    getHTMLTextLength(phoneCaptureDisclosureText) > MAX_DISCLOSURE_TEXT_LENGTH
  ) {
    throw new Error(
      `The attribute ${Attribute.PhoneCaptureDisclosureText} has a max length of ${MAX_DISCLOSURE_TEXT_LENGTH} characters (excluding HTML tags).`,
    );
  }
  if (!isPhoneCapture && PHONE_CAPTURE_STEPS.includes(showDiscountAt)) {
    throw new Error(
      `The attribute ${Attribute.ShowDiscountAt} can only be set to ${showDiscountAt} when ${Attribute.PhoneCapture} is enabled.`,
    );
  }
  if (!isPhoneCapture && PHONE_CAPTURE_STEPS.includes(saveDiscountAt)) {
    throw new Error(
      `The attribute ${Attribute.SaveDiscountAt} can only be set to ${saveDiscountAt} when ${Attribute.PhoneCapture} is enabled.`,
    );
  }
}

export enum Attribute {
  DiscountCodeCallback = 'discount-code-callback',
  StorefrontOrigin = 'storefront-origin',
  DevMode = 'dev-mode',
  ApiKey = 'api-key',
  Email = 'email',
  HideChange = 'hide-change',
  // Whether phone capture is on (default to false)
  PhoneCapture = 'phone-capture',
  // At which step to show the discount code
  ShowDiscountAt = 'show-discount-at',
  // At which step to save the discount code
  SaveDiscountAt = 'save-discount-at',
  // Disclosure text (HTML) to show the user at the Phone Consent Shown step
  PhoneCaptureDisclosureText = 'phone-capture-disclosure-text',
}

const requiredAttributes: Attribute[] = [
  Attribute.DiscountCodeCallback,
  Attribute.ApiKey,
];

export function validateRequiredAttributes(element: Element) {
  requiredAttributes.forEach((attribute) => {
    if (!element.getAttribute(attribute)) {
      logError(`Missing ${attribute} attribute`);
    }
  });
}
